import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';
import {
  Users, Settings, BarChart3, Calendar, DollarSign, Eye, EyeOff,
  Check, X, Search, ArrowLeft, ChevronDown, Download, Banknote,
  ShieldCheck, FileText, Camera, CreditCard, Award, Loader2, Activity,
  Trash2, Building2
} from 'lucide-react';
import AdminSessionReportDialog from '../../components/admin/AdminSessionReportDialog';
import AdminPaymentHealthWidget from '../../components/admin/AdminPaymentHealthWidget';
import AdminPiiComplianceWidget from '../../components/admin/AdminPiiComplianceWidget';

const API = getApiUrl();

function StatCard({ label, value, sub, icon: Icon, color = '#D4AF37' }) {
  return (
    <div className="bg-white border border-[#E8E2D2] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-xl font-light text-[#2A2319]">{value}</p>
          <p className="text-xs text-[#6B7280]">{label}</p>
          {sub && <p className="text-xs text-[#9CA3AF]">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

const PLAN_TIER_STYLES = {
  starter: { label: 'Starter', icon: '◆', bg: 'bg-zinc-500/10 text-[#374151] border-zinc-500/20' },
  pro:     { label: 'Pro',     icon: '★', bg: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30' },
  elite:   { label: 'Elite',   icon: '♛', bg: 'bg-purple-400/10 text-purple-300 border-purple-400/25' },
};

function SubscriptionPlanBadge({ planId, showIcon = true, className = '' }) {
  const pid = (planId || '').toLowerCase();
  const style = PLAN_TIER_STYLES[pid];
  if (!style) {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border border-zinc-600/30 text-[#6B7280] ${className}`}>
        Aucun plan
      </span>
    );
  }
  return (
    <span
      data-testid={`plan-badge-${pid}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${style.bg} ${className}`}
    >
      {showIcon && <span className="text-[11px]">{style.icon}</span>}
      {style.label}
    </span>
  );
}

export default function AdminCoaches() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [tab, setTab] = useState('coaches');
  const [coaches, setCoaches] = useState([]);
  const [totalCoaches, setTotalCoaches] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState({});
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionFilter, setSessionFilter] = useState('');
  const [payDialog, setPayDialog] = useState(null); // {coach_id, bookings}
  const [reportDialogBookingId, setReportDialogBookingId] = useState(null);
  const [payMethod, setPayMethod] = useState('virement');
  const [settingsEdit, setSettingsEdit] = useState({});
  const [detailCoach, setDetailCoach] = useState(null); // full coach detail
  const [detailLoading, setDetailLoading] = useState(false);

  // Pending approvals
  const [pendingCoaches, setPendingCoaches] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [docPreview, setDocPreview] = useState(null); // {coachId, docType, blobUrl, contentType}
  const [rejectDialog, setRejectDialog] = useState(null); // {coach}
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState({});
  const [removedCount, setRemovedCount] = useState(0);  // compteur "Retirés" (admin-only)

  // Stats globales (indépendantes du filtre actif) — un seul appel léger
  const [globalStats, setGlobalStats] = useState({ all: 0, active: 0, pending: 0, expired: 0 });

  const fetchGlobalStats = useCallback(async () => {
    try {
      // Récupère tous les coachs (incluant les retirés) UNE fois pour calculer les compteurs des pills
      const r = await fetch(`${API}/admin/coaches/list?include_removed=true&limit=100`, { headers });
      if (!r.ok) return;
      const d = await r.json();
      const all = d.coaches || [];
      const nonRemoved = all.filter(c => c.approval_status !== 'removed');
      setGlobalStats({
        all: nonRemoved.length,
        active: nonRemoved.filter(c => c.subscription_status === 'active').length,
        pending: nonRemoved.filter(c => c.subscription_status === 'pending').length,
        expired: nonRemoved.filter(c => c.subscription_status === 'expired').length,
      });
    } catch { /* best-effort */ }
  }, [token]);

  const fetchCoaches = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      // "to_pay" est un filtre CLIENT (montant à payer > 0), ne pas l'envoyer au backend
      if (statusFilter && statusFilter !== 'to_pay' && statusFilter !== 'removed') params.set('status', statusFilter);
      // Filtre dédié "Retirés" → backend ne renvoie QUE les approval_status=removed
      if (statusFilter === 'removed') params.set('only_removed', 'true');
      const res = await fetch(`${API}/admin/coaches/list?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCoaches(data.coaches || []);
        setTotalCoaches(data.total || 0);
      }
      // Compteur "Retirés" — appel parallèle léger (limit=1)
      try {
        const rmRes = await fetch(`${API}/admin/coaches/list?only_removed=true&limit=1`, { headers });
        if (rmRes.ok) {
          const rmData = await rmRes.json();
          setRemovedCount(rmData.total || 0);
        }
      } catch { /* compteur best-effort, ne bloque pas l'UI */ }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, search, statusFilter]);

  // Filtrage client : "À payer" = coachs avec pending_payout > 0
  const filteredCoaches = statusFilter === 'to_pay'
    ? coaches.filter(c => Number(c.pending_payout || 0) > 0)
    : statusFilter && statusFilter !== 'removed'
    ? coaches.filter(c => c.subscription_status === statusFilter)
    : coaches;

  // Compteurs pour les filtres pills.
  // ⚠️ "À payer" compte les SÉANCES en attente (granularité identique à l'onglet Séances)
  //    pour garantir la cohérence stricte du chiffre entre les deux vues.
  const pendingSessionsTotal = coaches.reduce(
    (acc, c) => acc + Number(c.session_counts?.to_pay || 0), 0
  );
  const pendingCoachesCount = coaches.filter(
    c => Number(c.pending_payout || 0) > 0
  ).length;

  const coachStats = {
    '': globalStats.all,
    active: globalStats.active,
    pending: globalStats.pending,
    expired: globalStats.expired,
    to_pay: pendingSessionsTotal,  // ← unifié avec l'onglet Séances
    removed: removedCount,
  };
  const COACH_FILTERS = [
    { value: '', label: 'Tous' },
    { value: 'active', label: 'Actifs' },
    { value: 'pending', label: 'En attente' },
    { value: 'expired', label: 'Expirés' },
    { value: 'to_pay', label: 'À payer' },
    { value: 'removed', label: 'Retirés' },
  ];

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/coaches/settings`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setSettingsEdit(data);
      }
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchFinancial = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/coaches/financial/summary`, { headers });
      if (res.ok) setFinancial(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sessionFilter) params.set('status', sessionFilter);
      params.set('limit', '50');
      const res = await fetch(`${API}/admin/coaches/sessions/all?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
        setSessionStats(data.status_stats || {});
        setTotalSessions(data.total || 0);
      }
    } catch (err) { console.error(err); }
  }, [token, sessionFilter]);

  useEffect(() => { fetchCoaches(); }, [fetchCoaches]);
  useEffect(() => { fetchGlobalStats(); }, [fetchGlobalStats]);
  useEffect(() => { fetchSettings(); fetchFinancial(); }, [fetchSettings, fetchFinancial]);
  useEffect(() => { if (tab === 'sessions') fetchSessions(); }, [tab, fetchSessions]);

  const fetchPendingCoaches = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await fetch(`${API}/admin/coaches/pending/list`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPendingCoaches(data.coaches || []);
      }
    } catch (err) { console.error(err); }
    setPendingLoading(false);
  }, [token]);

  useEffect(() => { fetchPendingCoaches(); }, [fetchPendingCoaches]);
  useEffect(() => { if (tab === 'pending') fetchPendingCoaches(); }, [tab, fetchPendingCoaches]);

  const previewDocument = async (coachId, docType) => {
    try {
      const res = await fetch(`${API}/admin/coaches/documents/${coachId}/${docType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Document non disponible');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setDocPreview({ coachId, docType, blobUrl, contentType: blob.type });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleApprove = async (coachId) => {
    setApproving(prev => ({ ...prev, [coachId]: 'approve' }));
    try {
      const res = await fetch(`${API}/admin/coaches/${coachId}/approve`, {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'approve' }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      toast.success('Coach approuvé — email envoyé');
      fetchPendingCoaches();
      fetchCoaches();
    } catch (err) { toast.error(err.message); }
    setApproving(prev => ({ ...prev, [coachId]: null }));
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    const coachId = rejectDialog.user_id;
    setApproving(prev => ({ ...prev, [coachId]: 'reject' }));
    try {
      const res = await fetch(`${API}/admin/coaches/${coachId}/approve`, {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'reject', rejection_reason: rejectReason }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      toast.success('Coach refusé — email envoyé');
      setRejectDialog(null);
      setRejectReason('');
      fetchPendingCoaches();
      fetchCoaches();
    } catch (err) { toast.error(err.message); }
    setApproving(prev => ({ ...prev, [coachId]: null }));
  };

  const toggleVisibility = async (coachId, current) => {
    try {
      await fetch(`${API}/admin/coaches/${coachId}`, {
        method: 'PUT', headers, body: JSON.stringify({ is_visible: !current }),
      });
      toast.success(!current ? 'Coach visible' : 'Coach masqué');
      fetchCoaches();
    } catch (err) { toast.error('Erreur'); }
  };

  const removeCoachStatus = async (coachId, coachName) => {
    const confirmed = window.confirm(
      `Retirer le statut coach de "${coachName}" ?\n\n` +
      `• Le compte utilisateur est CONSERVÉ (historique, sessions, paiements)\n` +
      `• Le profil coach est désactivé et masqué de la marketplace\n` +
      `• Le rôle "coach" est retiré (devient "user")\n` +
      `• Sessions futures non démarrées : plus accessibles côté coach\n` +
      `• Action tracée dans l'audit log admin\n\n` +
      `Le coach apparaîtra dans le filtre "Retirés" (action réversible via support).`
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API}/admin/coaches/${coachId}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Erreur serveur' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Statut coach retiré. Utilisateur conservé.');
      // Optimistic UI : la carte disparaît immédiatement de la liste courante
      // (le filtre "Retirés" la retrouvera après le refetch backend).
      setCoaches(prev => prev.filter(c => c.user_id !== coachId));
      setRemovedCount(n => n + 1);
      // Refresh canonique en tâche de fond
      fetchCoaches();
      fetchPendingCoaches();
      fetchGlobalStats();
    } catch (err) {
      toast.error(`Échec retrait : ${err.message}`);
    }
  };

  const saveSettings = async () => {
    try {
      const payload = {};
      if (settingsEdit.session_link_delay_minutes !== undefined && settingsEdit.session_link_delay_minutes !== '') {
        payload.session_link_delay_minutes = parseInt(settingsEdit.session_link_delay_minutes) || 0;
      }
      if (settingsEdit.subscription_plans) {
        // Clean & normalize: only send plans with at least one changed field
        const plansPayload = {};
        for (const [planId, cfg] of Object.entries(settingsEdit.subscription_plans)) {
          plansPayload[planId] = {
            label: cfg.label,
            price_eur: parseFloat(cfg.price_eur),
            commission_rate: parseFloat(cfg.commission_rate),
            is_active: !!cfg.is_active,
            is_popular: !!cfg.is_popular,
          };
        }
        payload.subscription_plans = plansPayload;
      }
      const res = await fetch(`${API}/admin/coaches/settings`, {
        method: 'PUT', headers,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Paramètres sauvegardés');
        fetchSettings();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) { toast.error('Erreur'); }
  };

  const togglePopularPlan = (planId) => {
    const plans = { ...(settingsEdit.subscription_plans || {}) };
    for (const pid of Object.keys(plans)) {
      plans[pid] = { ...plans[pid], is_popular: pid === planId };
    }
    setSettingsEdit({ ...settingsEdit, subscription_plans: plans });
  };

  const updatePlanField = (planId, field, value) => {
    const plans = { ...(settingsEdit.subscription_plans || {}) };
    plans[planId] = { ...(plans[planId] || {}), [field]: value };
    setSettingsEdit({ ...settingsEdit, subscription_plans: plans });
  };

  const markAsPaid = async () => {
    if (!payDialog) return;
    try {
      const res = await fetch(`${API}/admin/coaches/payouts`, {
        method: 'POST', headers,
        body: JSON.stringify({
          booking_ids: payDialog.bookings.map(b => b.booking_id),
          payment_method: payMethod,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setPayDialog(null);
        fetchCoaches();
        fetchFinancial();
        fetchSessions();
      }
    } catch (err) { toast.error('Erreur'); }
  };

  const openPayDialog = async (coach) => {
    // Fetch to_pay bookings for this coach
    try {
      const res = await fetch(`${API}/admin/coaches/sessions/all?status=to_pay&coach_id=${coach.user_id}&limit=100`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPayDialog({ coach, bookings: data.sessions || [] });
      }
    } catch (err) { toast.error('Erreur'); }
  };

  const fetchCoachDetail = async (coach) => {
    setDetailLoading(true);
    setDetailCoach(coach); // show dialog immediately with list data
    try {
      const res = await fetch(`${API}/admin/coaches/${coach.user_id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDetailCoach({ ...coach, ...data });
      }
    } catch (err) { console.error(err); }
    setDetailLoading(false);
  };

  const STATUS_LABELS = {
    pending_payment: 'En attente paiement',
    confirmed: 'Confirmée',
    completed: 'Réalisée',
    report_generated: 'Rapport généré',
    to_pay: 'À payer',
    coach_paid: 'Payé coach',
    cancelled: 'Annulée',
  };

  const STATUS_COLORS = {
    pending_payment: 'bg-yellow-500/10 text-yellow-400',
    confirmed: 'bg-blue-500/10 text-blue-400',
    completed: 'bg-cyan-500/10 text-cyan-400',
    report_generated: 'bg-purple-500/10 text-purple-400',
    to_pay: 'bg-orange-500/10 text-orange-400',
    coach_paid: 'bg-green-500/10 text-green-400',
    cancelled: 'bg-red-500/10 text-red-400',
  };

  return (
    <div className="min-h-screen bg-[#FBF9F3] pt-16 sm:pt-8 pb-32 px-4">
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-1">Administration</p>
            <h1 className="text-xl sm:text-2xl font-light text-[#2A2319]">Gestion des Coachs</h1>
          </div>
          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20">{totalCoaches} coachs</Badge>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="mb-6 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            <TabsList className="bg-white border border-[#E8E2D2] inline-flex sm:flex sm:flex-wrap w-auto min-w-max sm:min-w-0 gap-1">
              <TabsTrigger value="coaches" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Users className="w-4 h-4 mr-1.5" /> Coachs
              </TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Dossiers
                {pendingCoaches.length > 0 && (
                  <span className="ml-1.5 w-5 h-5 rounded-full bg-orange-500 text-[#2A2319] text-[10px] flex items-center justify-center font-bold">
                    {pendingCoaches.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="financial" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <DollarSign className="w-4 h-4 mr-1.5" /> Financier
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="admin-tab-payments" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Activity className="w-4 h-4 mr-1.5" /> Paiements
              </TabsTrigger>
              <TabsTrigger value="pii" data-testid="admin-tab-pii" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> Conformité PII
              </TabsTrigger>
              <TabsTrigger value="sessions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Calendar className="w-4 h-4 mr-1.5" /> Séances
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Settings className="w-4 h-4 mr-1.5" /> Paramètres
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Coaches tab */}
          <TabsContent value="coaches">
            {/* Barre de recherche */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un coach..." className="pl-10 bg-white border-[#E8E2D2] text-[#2A2319] rounded-xl" />
              </div>
            </div>

            {/* Filtres pills — cohérents avec l'onglet Séances */}
            <div className="flex flex-wrap gap-2 mb-2" data-testid="admin-coaches-filters">
              {COACH_FILTERS.map(f => (
                <button
                  key={f.value || 'all'}
                  data-testid={`admin-coaches-filter-${f.value || 'all'}`}
                  onClick={() => setStatusFilter(f.value)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                    statusFilter === f.value
                      ? 'bg-[#D4AF37] text-[#0A0A0B] font-medium'
                      : 'bg-white/5 text-[#4B5563] hover:bg-white/10'
                  }`}>
                  {f.label} ({coachStats[f.value] ?? 0})
                </button>
              ))}
            </div>

            {/* Clarification : quand "À payer" est actif, on explicite la granularité */}
            {statusFilter === 'to_pay' && (
              <p className="text-[11px] text-[#6B7280] mb-4 pl-1">
                {pendingSessionsTotal} séance{pendingSessionsTotal > 1 ? 's' : ''} en attente · répartie{pendingSessionsTotal > 1 ? 's' : ''} sur {pendingCoachesCount} coach{pendingCoachesCount > 1 ? 's' : ''}
              </p>
            )}

            <div className="space-y-3">
              {filteredCoaches.map(c => (
                <div key={c.user_id} className="bg-white border border-[#E8E2D2] rounded-xl p-4" data-testid={`admin-coach-${c.user_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-[#2A2319] font-medium truncate">{c.display_name}</h3>
                        <Badge className={c.subscription_status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}>
                          {c.subscription_status}
                        </Badge>
                        <SubscriptionPlanBadge planId={c.subscription_plan} />
                        {c.approval_status === 'removed' && (
                          <Badge className="bg-red-500/10 text-red-500 border border-red-500/20" data-testid={`removed-badge-${c.user_id}`}>
                            Retiré
                          </Badge>
                        )}
                      </div>
                      <p className="text-[#6B7280] text-xs">{c.email}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#4B5563]">
                        <span>Sessions: {c.session_counts?.confirmed || 0} conf. | {(c.session_counts?.completed || 0) + (c.session_counts?.to_pay || 0) + (c.session_counts?.coach_paid || 0)} réal.</span>
                        <span className="text-[#D4AF37]">CA: {c.total_revenue}€</span>
                        <span className="text-orange-400">À payer: {c.pending_payout}€</span>
                        <span className="text-green-400">Payé: {c.total_paid}€</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => fetchCoachDetail(c)}
                        data-testid={`detail-coach-${c.user_id}`}
                        className="p-2 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all" title="Voir le détail complet">
                        <FileText className="w-4 h-4" />
                      </button>
                      {c.approval_status !== 'removed' && (
                        <>
                          <button onClick={() => toggleVisibility(c.user_id, c.is_visible)}
                            data-testid={`toggle-visibility-${c.user_id}`}
                            className={`p-2 rounded-lg transition-all ${c.is_visible ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-[#6B7280]'}`}
                            title={c.is_visible ? 'Masquer de la marketplace' : 'Afficher dans la marketplace'}
                            aria-label={c.is_visible ? 'Masquer de la marketplace' : 'Afficher dans la marketplace'}>
                            {c.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => removeCoachStatus(c.user_id, c.full_name || c.email || c.user_id)}
                            data-testid={`remove-coach-${c.user_id}`}
                            className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                            title="Retirer le statut coach (compte utilisateur conservé)"
                            aria-label="Retirer le statut coach">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {c.approval_status === 'removed' && (
                        <span className="px-2.5 py-2 rounded-lg bg-[#F3EFE3] text-[#6B7280] text-[11px] font-medium" data-testid={`removed-info-${c.user_id}`}>
                          Profil retiré · {c.removed_at ? new Date(c.removed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                        </span>
                      )}
                      {c.pending_payout > 0 && (
                        <button onClick={() => openPayDialog(c)}
                          data-testid={`pay-coach-${c.user_id}`}
                          className="flex items-center gap-1.5 px-3 py-2 bg-[#D4AF37]/10 text-[#D4AF37] rounded-lg text-xs font-medium hover:bg-[#D4AF37]/20 transition-all">
                          <Banknote className="w-4 h-4" /> Payer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Pending Approvals tab */}
          <TabsContent value="pending">
            {pendingLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
              </div>
            ) : pendingCoaches.length === 0 ? (
              <div className="text-center py-16">
                <ShieldCheck className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
                <h3 className="text-[#2A2319] font-medium mb-1">Aucun dossier en attente</h3>
                <p className="text-[#6B7280] text-sm">Tous les dossiers ont été traités.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingCoaches.map(coach => {
                  const DOC_TYPES = [
                    { key: 'profile_photo', label: 'Photo de profil', icon: Camera, field: 'profile_photo_path' },
                    { key: 'id_document', label: 'Pièce d\'identité', icon: CreditCard, field: 'id_document_path' },
                    { key: 'coach_certificate', label: 'Certificat coach', icon: Award, field: 'coach_certificate_path' },
                    { key: 'business_proof', label: 'Justificatif d\'exercice', icon: Building2, field: 'business_proof_path' },
                  ];
                  return (
                    <div key={coach.user_id} className="bg-white border border-[#E8E2D2] rounded-xl overflow-hidden"
                      data-testid={`pending-coach-${coach.user_id}`}>
                      {/* Coach header */}
                      <div className="p-5 border-b border-[#F1ECDC]">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div>
                            <h3 className="text-[#2A2319] font-medium text-lg">{coach.display_name}</h3>
                            <p className="text-[#6B7280] text-sm">{coach.email}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {/* 🟢 Plan B (Fev 2026) — Badge statut dossier
                                  À valider (4/4 docs) vs Incomplet (manquants) */}
                              {coach.docs_complete ? (
                                <Badge
                                  className="text-[10px] tracking-wide bg-green-50 text-green-700 border-green-200"
                                  data-testid={`coach-status-ready-${coach.user_id}`}
                                >
                                  À valider
                                </Badge>
                              ) : (
                                <Badge
                                  className="text-[10px] tracking-wide bg-amber-50 text-amber-700 border-amber-200"
                                  data-testid={`coach-status-incomplete-${coach.user_id}`}
                                >
                                  Dossier incomplet — {(coach.missing_docs || []).length} doc(s) manquant(s)
                                </Badge>
                              )}
                              <SubscriptionPlanBadge planId={coach.subscription_plan} />
                              <Badge variant="outline" className="text-[#4B5563] border-[#E8E2D2] text-xs">
                                {coach.hourly_rate}€/h — {coach.session_duration} min
                              </Badge>
                              {coach.certification_number && (
                                <Badge variant="outline" className="text-[#D4AF37] border-[#D4AF37]/20 text-xs">
                                  Cert: {coach.certification_number}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-[#9CA3AF] text-xs">
                            Inscrit le {new Date(coach.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        {/* Bio */}
                        <p className="text-[#4B5563] text-sm mt-3 line-clamp-2">{coach.bio}</p>
                        {/* Specialties */}
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {(coach.specialties || []).map(s => (
                            <span key={s} className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-xs">{s}</span>
                          ))}
                        </div>
                      </div>

                      {/* Documents section */}
                      <div className="p-5 bg-[#FAF7EE]">
                        <p className="text-[#4B5563] text-xs font-medium uppercase tracking-wider mb-3">Justificatifs déposés</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {DOC_TYPES.map(({ key, label, icon: Icon, field }) => {
                            const hasDoc = !!coach[field];
                            return (
                              <button key={key}
                                data-testid={`view-doc-${coach.user_id}-${key}`}
                                onClick={() => hasDoc && previewDocument(coach.user_id, key)}
                                disabled={!hasDoc}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                  hasDoc
                                    ? 'border-green-500/20 bg-green-500/5 hover:bg-green-500/10 cursor-pointer'
                                    : 'border-red-500/20 bg-red-500/5 opacity-60 cursor-not-allowed'
                                }`}>
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  hasDoc ? 'bg-green-500/10' : 'bg-red-500/10'
                                }`}>
                                  {hasDoc ? <Icon className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-red-400" />}
                                </div>
                                <div className="min-w-0">
                                  <p className={`text-sm font-medium ${hasDoc ? 'text-green-400' : 'text-red-400'}`}>{label}</p>
                                  <p className="text-[#9CA3AF] text-xs">{hasDoc ? 'Cliquer pour visualiser' : 'Non déposé'}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="p-5 flex flex-col sm:flex-row gap-3 border-t border-[#F1ECDC]">
                        <button
                          data-testid={`approve-coach-${coach.user_id}`}
                          onClick={() => handleApprove(coach.user_id)}
                          disabled={!!approving[coach.user_id] || !coach.docs_complete}
                          title={!coach.docs_complete ? 'Dossier incomplet — le coach doit déposer tous les justificatifs' : undefined}
                          className="flex-1 py-3 bg-green-500 text-[#2A2319] rounded-full font-medium text-sm hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
                          {approving[coach.user_id] === 'approve' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          {coach.docs_complete ? 'Approuver' : 'Approbation bloquée'}
                        </button>
                        <button
                          data-testid={`reject-coach-${coach.user_id}`}
                          onClick={() => setRejectDialog(coach)}
                          disabled={!!approving[coach.user_id]}
                          className="flex-1 py-3 bg-white/5 text-red-400 border border-red-500/20 rounded-full font-medium text-sm hover:bg-red-500/10 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                          <X className="w-4 h-4" /> Refuser
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Financial tab */}
          <TabsContent value="financial">
            {financial && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <StatCard label="CA total séances" value={`${financial.total_revenue}€`} icon={DollarSign} />
                  <StatCard label="Commission plateforme" value={`${financial.platform_commission}€`} icon={BarChart3} color="#22c55e" />
                  <StatCard label="Total dû coachs" value={`${financial.total_coach_payout}€`} icon={Users} color="#f97316" />
                  <StatCard label="À payer" value={`${financial.pending_payouts.amount}€`} sub={`${financial.pending_payouts.count} séance(s)`} icon={Banknote} color="#f97316" />
                  <StatCard label="Déjà payé" value={`${financial.completed_payouts.amount}€`} sub={`${financial.completed_payouts.count} séance(s)`} icon={Check} color="#22c55e" />
                  <StatCard label="Sessions totales" value={financial.total_sessions} icon={Calendar} />
                </div>
              </div>
            )}
          </TabsContent>

          {/* Payments health tab */}
          <TabsContent value="payments">
            <AdminPaymentHealthWidget />
          </TabsContent>

          {/* PII compliance tab */}
          <TabsContent value="pii">
            <AdminPiiComplianceWidget />
          </TabsContent>

          {/* Sessions tab */}
          <TabsContent value="sessions">
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => setSessionFilter('')}
                className={`px-3 py-1.5 rounded-full text-xs transition-all ${!sessionFilter ? 'bg-[#D4AF37] text-[#0A0A0B] font-medium' : 'bg-white/5 text-[#4B5563]'}`}>
                Toutes ({totalSessions})
              </button>
              {Object.entries(sessionStats).map(([status, data]) => (
                <button key={status} onClick={() => setSessionFilter(status)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${sessionFilter === status ? 'bg-[#D4AF37] text-[#0A0A0B] font-medium' : 'bg-white/5 text-[#4B5563]'}`}>
                  {STATUS_LABELS[status] || status} ({data.count})
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {sessions.map(s => (
                <div key={s.booking_id} className="bg-white border border-[#E8E2D2] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2"
                  data-testid={`admin-session-${s.booking_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[#2A2319] text-sm truncate">{s.coach_name}</span>
                      <span className="text-[#9CA3AF]">→</span>
                      <span className="text-[#374151] text-sm truncate">{s.client_name || s.client_email}</span>
                      {s.theme_label && (
                        <span className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 truncate max-w-[140px]">
                          {s.theme_label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-[#6B7280]">
                      <span>{s.date} {s.start_time}</span>
                      <span className="text-[#D4AF37]">{s.amount}€</span>
                      {s.coach_payout_amount != null && (
                        <span className="text-[#4B5563]">Coach: {s.coach_payout_amount}€</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={STATUS_COLORS[s.status] || 'bg-white/5 text-[#4B5563]'}>
                      {STATUS_LABELS[s.status] || s.status}
                    </Badge>
                    {s.has_ai_report && (
                      <button
                        data-testid={`admin-view-report-${s.booking_id}`}
                        onClick={() => setReportDialogBookingId(s.booking_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/25 transition-colors"
                        title="Voir les 3 rapports IA (global + feedback coach + feedback utilisateur)"
                      >
                        <FileText className="w-3 h-3" /> Rapports IA
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-[#6B7280] text-sm text-center py-8">Aucune séance trouvée.</p>
              )}
            </div>
          </TabsContent>

          {/* Settings tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* === Plans d'abonnement coach (3-tier) === */}
              <div className="bg-white border border-[#E8E2D2] rounded-xl p-6" data-testid="admin-subscription-plans-section">
                <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <h3 className="text-[#2A2319] font-medium text-base">Plans d'abonnement coach</h3>
                    <p className="text-[#6B7280] text-xs mt-1">
                      Modèle hybride : chaque coach paie un abonnement mensuel <span className="text-[#4B5563]">ET</span> une commission par session.
                      <span className="text-[#9CA3AF]"> Aucune valeur hardcodée — modifiable en temps réel.</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['starter', 'pro', 'elite'].map((planId) => {
                    const plan = (settingsEdit.subscription_plans || {})[planId] || {};
                    const tierConfig = {
                      starter: { accent: '#9CA3AF', icon: '◆', subtitle: 'Entrée de gamme' },
                      pro:     { accent: '#D4AF37', icon: '★', subtitle: 'Équilibre recommandé' },
                      elite:   { accent: '#B794F6', icon: '♛', subtitle: 'Premium' },
                    }[planId];
                    return (
                      <div
                        key={planId}
                        data-testid={`plan-card-${planId}`}
                        className={`relative bg-[#FBF9F3] border rounded-xl p-5 transition-all ${
                          plan.is_popular ? 'border-[#D4AF37]/60 shadow-[0_0_0_1px_rgba(212,175,55,0.3)]' : 'border-[#E8E2D2]'
                        }`}
                      >
                        {plan.is_popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#D4AF37] text-[#0A0A0B] text-[10px] font-semibold rounded-full tracking-wide">
                            RECOMMANDÉ
                          </div>
                        )}
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-2xl" style={{ color: tierConfig.accent }}>{tierConfig.icon}</span>
                          <input
                            data-testid={`plan-label-${planId}`}
                            value={plan.label || ''}
                            onChange={(e) => updatePlanField(planId, 'label', e.target.value)}
                            className="bg-transparent text-[#2A2319] font-medium text-lg outline-none focus:ring-0 border-b border-transparent focus:border-[#92710A]/40 flex-1"
                          />
                        </div>
                        <p className="text-[11px] text-[#6B7280] mb-4 uppercase tracking-widest">{tierConfig.subtitle}</p>

                        <div className="space-y-3">
                          <div>
                            <label className="text-[11px] text-[#4B5563] mb-1 block uppercase tracking-wide">Prix mensuel (€)</label>
                            <div className="flex items-center gap-2 bg-white border border-[#E8E2D2] rounded-lg px-3 py-2">
                              <input
                                data-testid={`plan-price-${planId}`}
                                type="number"
                                step="0.01"
                                value={plan.price_eur ?? ''}
                                onChange={(e) => updatePlanField(planId, 'price_eur', e.target.value)}
                                className="bg-transparent text-[#2A2319] outline-none flex-1 text-sm"
                              />
                              <span className="text-[#6B7280] text-sm">€</span>
                            </div>
                          </div>

                          <div>
                            <label className="text-[11px] text-[#4B5563] mb-1 block uppercase tracking-wide">Commission par session (%)</label>
                            <div className="flex items-center gap-2 bg-white border border-[#E8E2D2] rounded-lg px-3 py-2">
                              <input
                                data-testid={`plan-commission-${planId}`}
                                type="number"
                                step="0.5"
                                value={plan.commission_rate ?? ''}
                                onChange={(e) => updatePlanField(planId, 'commission_rate', e.target.value)}
                                className="bg-transparent text-[#2A2319] outline-none flex-1 text-sm"
                              />
                              <span className="text-[#6B7280] text-sm">%</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-[#F1ECDC]">
                            <button
                              data-testid={`plan-active-toggle-${planId}`}
                              onClick={() => updatePlanField(planId, 'is_active', !plan.is_active)}
                              className={`text-xs flex items-center gap-1.5 ${plan.is_active ? 'text-emerald-400' : 'text-[#6B7280]'}`}
                              type="button"
                            >
                              <span className={`w-2 h-2 rounded-full ${plan.is_active ? 'bg-emerald-400' : 'bg-zinc-600'}`}></span>
                              {plan.is_active ? 'Actif' : 'Désactivé'}
                            </button>
                            <label className="text-xs flex items-center gap-1.5 text-[#4B5563] cursor-pointer">
                              <input
                                data-testid={`plan-popular-${planId}`}
                                type="radio"
                                name="popular-plan"
                                checked={!!plan.is_popular}
                                onChange={() => togglePopularPlan(planId)}
                                className="accent-[#D4AF37]"
                              />
                              Recommandé
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                  <p className="text-[11px] text-[#9CA3AF]">
                    💡 La commission est calculée automatiquement en interne lors de la génération du rapport IA — Stripe est inchangé.
                  </p>
                  <button
                    onClick={saveSettings}
                    data-testid="save-settings-btn"
                    className="px-5 py-2 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all"
                  >
                    Enregistrer les plans
                  </button>
                </div>
              </div>

              {/* === Autres paramètres === */}
              <div className="bg-white border border-[#E8E2D2] rounded-xl p-6 max-w-lg">
                <h3 className="text-[#2A2319] font-medium mb-4 text-base">Autres paramètres</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-[#374151] mb-2 block">Délai lien de session (minutes)</label>
                    <Input type="number" data-testid="settings-link-delay"
                      value={settingsEdit.session_link_delay_minutes ?? ''}
                      onChange={(e) => setSettingsEdit({ ...settingsEdit, session_link_delay_minutes: e.target.value })}
                      className="bg-[#FBF9F3] border-[#E8E2D2] text-[#2A2319] rounded-xl" min="0" />
                    <p className="text-[#6B7280] text-xs mt-1">0 = lien visible immédiatement. X = visible X minutes avant la session.</p>
                  </div>
                  <button onClick={saveSettings} data-testid="save-other-settings-btn"
                    className="px-6 py-2.5 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Coach Detail Dialog */}
      <Dialog open={!!detailCoach} onOpenChange={(open) => !open && setDetailCoach(null)}>
        <DialogContent className="bg-white border-[#E8E2D2] text-[#2A2319] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2A2319] text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" /> {detailCoach?.display_name}
            </DialogTitle>
          </DialogHeader>
          {detailCoach && (
            <div className="space-y-5 pt-2">
              {detailLoading && (
                <div className="flex items-center gap-2 text-[#4B5563] text-xs">
                  <Loader2 className="w-3 h-3 animate-spin" /> Chargement...
                </div>
              )}

              {/* PROFIL */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium uppercase tracking-wider text-[#D4AF37]">Profil</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#6B7280] text-xs">Nom</p>
                    <p className="text-[#2A2319]">{detailCoach.display_name}</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Email</p>
                    <p className="text-[#2A2319] text-sm break-all">{detailCoach.email}</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Téléphone</p>
                    <p className="text-[#2A2319]">{detailCoach.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">LinkedIn</p>
                    {detailCoach.linkedin_url ? (
                      <a href={detailCoach.linkedin_url} target="_blank" rel="noreferrer" className="text-[#D4AF37] text-sm hover:underline truncate block">{detailCoach.linkedin_url}</a>
                    ) : <p className="text-[#9CA3AF]">—</p>}
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Tarif horaire</p>
                    <p className="text-[#2A2319]">{detailCoach.hourly_rate}€/h</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Durée session</p>
                    <p className="text-[#2A2319]">{detailCoach.session_duration} min</p>
                  </div>
                </div>
                {detailCoach.bio && (
                  <div>
                    <p className="text-[#6B7280] text-xs mb-1">Bio</p>
                    <p className="text-[#374151] text-sm leading-relaxed">{detailCoach.bio}</p>
                  </div>
                )}
                {detailCoach.specialties?.length > 0 && (
                  <div>
                    <p className="text-[#6B7280] text-xs mb-1">Spécialités</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailCoach.specialties.map(s => (
                        <span key={s} className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-xs">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {detailCoach.certifications?.length > 0 && (
                  <div>
                    <p className="text-[#6B7280] text-xs mb-1">Certifications</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailCoach.certifications.map(c => (
                        <span key={c} className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded text-xs">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* DOCUMENTS */}
              <div className="space-y-3 border-t border-[#F1ECDC] pt-4">
                <h4 className="text-xs font-medium uppercase tracking-wider text-[#D4AF37]">Documents</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { key: 'profile_photo', label: 'Photo de profil', icon: Camera, field: 'profile_photo_path' },
                    { key: 'id_document', label: 'Pièce d\'identité', icon: CreditCard, field: 'id_document_path' },
                    { key: 'coach_certificate', label: 'Certificat', icon: Award, field: 'coach_certificate_path' },
                    { key: 'business_proof', label: 'Just. d\'exercice', icon: Building2, field: 'business_proof_path' },
                  ].map(({ key, label, icon: Icon, field }) => {
                    const hasDoc = !!detailCoach[field];
                    return (
                      <button key={key}
                        data-testid={`detail-doc-${key}`}
                        onClick={() => hasDoc && previewDocument(detailCoach.user_id, key)}
                        disabled={!hasDoc}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm ${
                          hasDoc ? 'border-green-500/20 bg-green-500/5 hover:bg-green-500/10' : 'border-[#F1ECDC] bg-white/[0.02] opacity-50'
                        }`}>
                        <Icon className={`w-4 h-4 flex-shrink-0 ${hasDoc ? 'text-green-400' : 'text-[#9CA3AF]'}`} />
                        <span className={hasDoc ? 'text-green-400' : 'text-[#9CA3AF]'}>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* PAIEMENT */}
              <div className="space-y-3 border-t border-[#F1ECDC] pt-4">
                <h4 className="text-xs font-medium uppercase tracking-wider text-[#D4AF37]">Informations de paiement</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#6B7280] text-xs">Plan d'abonnement</p>
                    <div className="mt-1">
                      <SubscriptionPlanBadge planId={detailCoach.subscription_plan} />
                    </div>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Mode de paiement</p>
                    <p className="text-[#2A2319]">{detailCoach.payment_method === 'paypal' ? 'PayPal' : detailCoach.payment_method === 'virement' ? 'Virement bancaire' : '—'}</p>
                  </div>
                  {detailCoach.payment_info?.iban && (
                    <div className="col-span-2">
                      <p className="text-[#6B7280] text-xs">IBAN</p>
                      <p className="text-[#2A2319] font-mono text-xs">{detailCoach.payment_info.iban}</p>
                    </div>
                  )}
                  {detailCoach.payment_info?.bic && (
                    <div>
                      <p className="text-[#6B7280] text-xs">BIC</p>
                      <p className="text-[#2A2319] font-mono text-xs">{detailCoach.payment_info.bic}</p>
                    </div>
                  )}
                  {detailCoach.payment_info?.titulaire && (
                    <div>
                      <p className="text-[#6B7280] text-xs">Titulaire</p>
                      <p className="text-[#2A2319]">{detailCoach.payment_info.titulaire}</p>
                    </div>
                  )}
                  {detailCoach.payment_info?.paypal_email && (
                    <div className="col-span-2">
                      <p className="text-[#6B7280] text-xs">Email PayPal</p>
                      <p className="text-[#2A2319]">{detailCoach.payment_info.paypal_email}</p>
                    </div>
                  )}
                  {!detailCoach.payment_method && !detailCoach.payment_info && (
                    <p className="col-span-2 text-[#9CA3AF] text-xs italic">Aucune information de paiement renseignée</p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/10 rounded-lg p-3 text-center">
                    <p className="text-[#D4AF37] font-medium">{detailCoach.total_revenue || 0}€</p>
                    <p className="text-[#6B7280] text-xs">CA total</p>
                  </div>
                  <div className="bg-orange-500/5 border border-orange-500/10 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">{detailCoach.pending_payout || 0}€</p>
                    <p className="text-[#6B7280] text-xs">À payer</p>
                  </div>
                  <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3 text-center">
                    <p className="text-green-400 font-medium">{detailCoach.total_paid || 0}€</p>
                    <p className="text-[#6B7280] text-xs">Payé</p>
                  </div>
                </div>
              </div>

              {/* STATUT */}
              <div className="space-y-3 border-t border-[#F1ECDC] pt-4">
                <h4 className="text-xs font-medium uppercase tracking-wider text-[#D4AF37]">Statut</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#6B7280] text-xs">Abonnement</p>
                    <Badge className={detailCoach.subscription_status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}>
                      {detailCoach.subscription_status || '—'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Approbation</p>
                    <Badge className={detailCoach.approval_status === 'approved' ? 'bg-green-500/10 text-green-400' : detailCoach.approval_status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}>
                      {detailCoach.approval_status === 'approved' ? 'Approuvé' : detailCoach.approval_status === 'rejected' ? 'Refusé' : detailCoach.approval_status || '—'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Visibilité</p>
                    <p className={detailCoach.is_visible ? 'text-green-400' : 'text-red-400'}>{detailCoach.is_visible ? 'Visible' : 'Masqué'}</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs">Inscription</p>
                    <p className="text-[#2A2319] text-xs">{detailCoach.created_at ? new Date(detailCoach.created_at).toLocaleDateString('fr-FR') : '—'}</p>
                  </div>
                  {detailCoach.subscription_end_date && (
                    <div>
                      <p className="text-[#6B7280] text-xs">Fin abonnement</p>
                      <p className="text-[#2A2319] text-xs">{new Date(detailCoach.subscription_end_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[#6B7280] text-xs">Note moyenne</p>
                    <p className="text-[#2A2319]">{detailCoach.rating_avg || 0}/5 ({detailCoach.ratings_count || 0} avis)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={!!payDialog} onOpenChange={(open) => !open && setPayDialog(null)}>
        <DialogContent className="bg-white border-[#E8E2D2] text-[#2A2319] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2A2319] text-lg">Reversement — {payDialog?.coach.display_name}</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-[#4B5563] text-sm">
                {payDialog.bookings.length} séance(s) à payer pour un total de{' '}
                <span className="text-[#D4AF37] font-medium">
                  {payDialog.bookings.reduce((sum, b) => sum + (b.coach_payout_amount || 0), 0).toFixed(2)}€
                </span>
              </p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {payDialog.bookings.map(b => (
                  <div key={b.booking_id} className="flex justify-between text-xs bg-white/5 rounded p-2">
                    <span className="text-[#4B5563]">{b.date} {b.start_time}</span>
                    <span className="text-[#2A2319]">{b.coach_payout_amount}€</span>
                  </div>
                ))}
              </div>
              <div>
                <label className="text-sm text-[#374151] mb-2 block">Mode de paiement</label>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-[#FBF9F3] border border-[#E8E2D2] rounded-xl text-[#2A2319] text-sm">
                  <option value="virement">Virement bancaire</option>
                  <option value="paypal">PayPal</option>
                </select>
              </div>
              <button onClick={markAsPaid} data-testid="confirm-payout-btn"
                className="w-full py-3 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
                Confirmer le paiement
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document preview dialog */}
      <Dialog open={!!docPreview} onOpenChange={(open) => {
        if (!open) {
          if (docPreview?.blobUrl) URL.revokeObjectURL(docPreview.blobUrl);
          setDocPreview(null);
        }
      }}>
        <DialogContent className="bg-white border-[#E8E2D2] text-[#2A2319] max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-[#2A2319] text-lg">
              {docPreview?.docType === 'profile_photo' && 'Photo de profil'}
              {docPreview?.docType === 'id_document' && 'Pièce d\'identité'}
              {docPreview?.docType === 'coach_certificate' && 'Certificat coach'}
              {docPreview?.docType === 'business_proof' && 'Justificatif d\'exercice professionnel'}
            </DialogTitle>
          </DialogHeader>
          {docPreview && (
            <div className="mt-2">
              {docPreview.contentType?.startsWith('image/') ? (
                <img src={docPreview.blobUrl} alt="Document"
                  className="w-full max-h-[60vh] object-contain rounded-lg bg-[#FBF9F3]" />
              ) : docPreview.contentType === 'application/pdf' ? (
                <iframe src={docPreview.blobUrl} title="Document PDF"
                  className="w-full h-[60vh] rounded-lg border border-[#E8E2D2]" />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-[#6B7280] mx-auto mb-3" />
                  <p className="text-[#4B5563] text-sm">Aperçu non disponible pour ce format.</p>
                  <a href={docPreview.blobUrl} download className="text-[#D4AF37] text-sm underline mt-2 inline-block">
                    Télécharger
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(open) => { if (!open) { setRejectDialog(null); setRejectReason(''); } }}>
        <DialogContent className="bg-white border-[#E8E2D2] text-[#2A2319] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2A2319] text-lg">Refuser le dossier — {rejectDialog?.display_name}</DialogTitle>
          </DialogHeader>
          {rejectDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-[#4B5563] text-sm">
                Le coach recevra un email avec le motif du refus. Il pourra re-soumettre son dossier.
              </p>
              <div>
                <label className="text-sm text-[#374151] mb-2 block">Motif du refus</label>
                <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex: Photo de profil non conforme, certificat illisible..."
                  className="w-full px-3 py-2.5 bg-[#FBF9F3] border border-[#E8E2D2] rounded-xl text-[#2A2319] text-sm min-h-[80px] focus:outline-none focus:border-red-500/40"
                  data-testid="reject-reason-input" />
              </div>
              <button onClick={handleReject} disabled={approving[rejectDialog.user_id] === 'reject'}
                data-testid="confirm-reject-btn"
                className="w-full py-3 bg-red-500 text-[#2A2319] rounded-full font-medium text-sm hover:bg-red-400 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                {approving[rejectDialog.user_id] === 'reject' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                Confirmer le refus
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Session AI Report Dialog */}
      {reportDialogBookingId && (
        <AdminSessionReportDialog
          bookingId={reportDialogBookingId}
          onClose={() => setReportDialogBookingId(null)}
        />
      )}
    </div>
  );
}
